import { TitleCasePipe } from "@angular/common";
import { Directive, ElementRef, Host, Input, OnDestroy, OnInit, Optional, Renderer2 } from "@angular/core";
import { NgControl, NgSelectOption, SelectControlValueAccessor } from "@angular/forms";
import { Subject } from "rxjs";
import { concatMap, distinctUntilKeyChanged, map } from "rxjs/operators";
import { Connote, ConnoteSite as DispatchSite } from "src/app/model/dispatch.model";
import { ConnoteSiteService } from "src/app/services/connote-site.service";
import { ReadLocationPipe } from "src/app/theme/pipes/read/read-location.pipe";


interface ConnoteSite extends DispatchSite {
  /**
   * Number of related run plan activities. This is enabled by adding
   * `?include=runPlanActivitiesCount` to the query param.
   */
  run_plan_activities_count: number;

  location: DispatchSite['location'];
}

@Directive({
  selector: 'select[appConnoteSitePicker]'
})
export class ConnoteSitePickerDirective implements OnInit, OnDestroy {
  /**
   * Specify the connote by which site options will be shown.
   */
  @Input()
  public set connote(input: Pick<Connote, 'id'>) {
    this.connoteSource.next(input);
  }

  private connoteSource = new Subject<Pick<Connote, 'id'>>();

  private locReader = new ReadLocationPipe();

  private titlecase = new TitleCasePipe();

  private originalValue: ConnoteSite['id'] | null = null;

  public constructor(
    @Optional() @Host() private select: NgControl | null,
    private sites    : ConnoteSiteService,
    private element  : ElementRef<HTMLSelectElement>,
    private renderer : Renderer2
  ) {
    this.connoteSource
      .pipe(
        distinctUntilKeyChanged('id'),
        concatMap((connote) => this.sites.get<ConnoteSite>({
          per_page: -1,
          includes: [
            'runPlanActivitiesCount',
            'location',
            'runAction'
          ],
          filters: {
            'filter[connote_id]': connote.id
          }
        })),
        map((page) => page.data)
      )
      .subscribe((sites) => {
        this.element.nativeElement?.childNodes.forEach((node) => this.renderer.removeChild(this.element.nativeElement, node));

        this.appendOption('New Pickup or Delivery Site', null);

        const doesMatchOption = sites.map((site) => {
          const location = this.locReader.transform(site.location);
          const action   = site.run_action?.label
            ? this.titlecase.transform(site.run_action.label)
            : '';

          const label      = `${action} at ${location}`;
          const value      = site.id;
          // const value      = site;
          const isDisabled = site.run_plan_activities_count > 0 && site.id !== this.originalValue;

          return { label, value, isDisabled };
        })
        .map(({ label, value, isDisabled }) => {
          this.appendOption(label, value, isDisabled);
          return value;
        })
        .some((siteId) => siteId === this.select?.value);

        if(!doesMatchOption) {
          this.select?.control.setValue(null);
          this.select?.control.markAsDirty();
        }
      });
  }

  public ngOnInit() {
    this.originalValue = this.select?.value;
  }

  public ngOnDestroy() {
    this.connoteSource.complete();
  }

  /**
   * Append an option to the select host.
   * 
   * @param label Option text.
   * @param value Option value.
   */
  // private appendOption(label: string, value: ConnoteSite, isDisabled = false) {
  private appendOption(label: string, value: number, isDisabled = false) {
    const option     = this.renderer.createElement('option');
    const optionText = this.renderer.createText(label);
    this.renderer.setValue(option, `${value}`);
    this.renderer.setProperty(option, 'disabled', isDisabled);

    this.renderer.appendChild(option, optionText);
    this.renderer.appendChild(this.element.nativeElement, option);

    const ngOption = new NgSelectOption(new ElementRef(option), this.renderer, <SelectControlValueAccessor> this.select?.valueAccessor);
    ngOption.ngValue = value;
  }
}
